import * as React from "react";

import type { SvgIconComponent } from "@mui/icons-material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MicIcon from "@mui/icons-material/Mic";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import Box from "@mui/material/Box";

import { loginPromo, misc } from "app/themes/colors";
import ScribesAppPreview from "assets/icons/ios/scribes-app-preview.png";

import { artworkHeightSx } from "./login-promo-sizing";
import VoiceCallPreview from "./voice-call-preview.component";

export interface LoginPromoBenefit {
  Icon: SvgIconComponent;
  title: string;
  body: string;
}

/**
 * One product the login screen's right-hand panel rotates through. Every
 * promo shares the same layout (label pill, notice, headline, three benefits,
 * artwork), so adding a launch is one entry here rather than new layout work.
 * Keep each promo at three benefits: the slides are stacked in one grid cell,
 * and a taller one makes the shorter one float in empty space.
 */
export interface LoginPromo {
  /** Stable id: persisted to localStorage and sent to RUM, so never rename. */
  key: string;
  label: string;
  accent: string;
  /**
   * Panel colour while this promo shows, so each product reads as its own
   * section. Omit it to keep the panel's plum (`sideMenu.main`).
   */
  background?: string;
  notice: string;
  headline: React.ReactNode;
  /** Plain-text headline for the slide's accessible name. */
  headlineText: string;
  benefits: LoginPromoBenefit[];
  Artwork: React.ComponentType;
}

function ScribesArtwork() {
  return (
    <Box
      component="img"
      src={ScribesAppPreview}
      alt="Suno Scribes recording a visit and drafting the note on a phone"
      // Below lg the panel is too narrow to carry text and artwork
      // side by side — the install card gets pushed off screen.
      display={{ md: "none", lg: "block" }}
      width="auto"
      flexShrink={0}
      sx={{
        // Scales with the viewport so the panel still fits, copyright
        // included, on a 768px-tall laptop screen. The 640px cap only bites
        // on very tall screens.
        ...artworkHeightSx,
        // Percentages so the shadow keeps tracking the artwork's own
        // 38px corners as the image scales (38/244 by 38/452).
        borderRadius: "15.6% / 8.4%",
        boxShadow: "0 24px 60px rgba(0, 0, 0, 0.35)"
      }}
    />
  );
}

export const LOGIN_PROMOS: LoginPromo[] = [
  {
    key: "scribes",
    label: "Ambient Scribe",
    accent: misc.chatLavender,
    // A licence cannot be activated from the app, so the panel has to say
    // where it can be — see PR #6877 discussion.
    notice:
      "Sign up in your browser to try Suno Scribes — or ask us for a demo.",
    headline: <>Try Suno Scribes on&nbsp;mobile</>,
    headlineText: "Try Suno Scribes on mobile",
    benefits: [
      {
        Icon: MicIcon,
        title: "Record anywhere in the clinic",
        body: "Booth, fitting room, home visit — your phone is the mic."
      },
      {
        Icon: DescriptionOutlinedIcon,
        title: "The note writes itself",
        body: "A structured draft lands in the chart, ready to review."
      },
      {
        Icon: ScheduleOutlinedIcon,
        title: "Leave on time",
        body: "No evening catch-up on the day's documentation."
      }
    ],
    Artwork: ScribesArtwork
  },
  {
    key: "voice",
    label: "Suno Voice AI",
    accent: loginPromo.voiceTeal,
    background: loginPromo.voiceDeepTeal,
    // Voice is an upsell with no self-serve sign-up, so the ask is a demo
    // through support.
    notice: "Reach out to support to schedule a demo.",
    headline: <>Know who’s calling before you pick&nbsp;up.</>,
    headlineText: "Know who’s calling before you pick up.",
    benefits: [
      {
        Icon: CallOutlinedIcon,
        title: "Replaces your phone system",
        body: "Desk handsets and the mobile app, shipped and set up by Suno."
      },
      {
        Icon: AutoAwesomeIcon,
        title: "AI summaries & transcripts",
        body: "Never replay a call again."
      },
      {
        Icon: NorthEastIcon,
        title: "Click-to-call",
        body: "Dial from any chart in Suno."
      }
    ],
    Artwork: VoiceCallPreview
  }
];
